import {
  DemandeHabilitationSortInput,
  GetDemandesHabilitationFilteredBackOfficeGQL,
  DemandeHabilitation as DemandeHabilitationGql,
  SortEnumType,
  GetDemandesHabilitationCountByStatutBackOfficeGQL,
  DemandeHabilitationStatutEnum,
  DemandeHabilitationAffecterGQL,
  DemandeHabilitationRefuserGQL,
} from '@graphql/generated';
import { inject, Injectable } from '@angular/core';
import { Pagination } from '@lib/models/pagination.model';
import { combineLatest, lastValueFrom, map, Observable } from 'rxjs';
import { DemandeHabilitationsBackOfficeService } from '../demande-habilitations-backoffice.service';
import { HabilitationTabsCounters } from '../../models/habilitation-tabs-counters.model';
import { DemandeHabilitationStatutMapper } from '@lib/mappers/graphql/demande-habilitation-statut-mapper';
import { HabilitationStatut } from '@lib/models/demande-habilitation/types/habilitation-statut.type';
import { DemandeHabilitationMapper } from '@lib/mappers/graphql/demande-habilitation-mapper';
import { DemandeHabilitation } from '@lib/models/demande-habilitation/demande-habilitation.model';

@Injectable({ providedIn: 'root' })
export class GraphQlDemandeHabilitationsBackOfficeService extends DemandeHabilitationsBackOfficeService {
  private readonly getDemandesHabilitationCountByStatutGql = inject(
    GetDemandesHabilitationCountByStatutBackOfficeGQL
  );
  private readonly getDemandesHabilitationFilteredBackOfficeGql = inject(
    GetDemandesHabilitationFilteredBackOfficeGQL
  );
  private readonly demandeHabilitationAffecterGql = inject(
    DemandeHabilitationAffecterGQL
  );
  private readonly demandeHabilitationRefuserGql = inject(DemandeHabilitationRefuserGQL);

  private readonly aAffecterStatutsGql: DemandeHabilitationStatutEnum[] =
    this.aAffecterStatuts.map(s => DemandeHabilitationStatutMapper.toGraphQl(s)!);

  private readonly aControlerGql: DemandeHabilitationStatutEnum[] =
    this.aControlerStatuts.map(s => DemandeHabilitationStatutMapper.toGraphQl(s)!);

  private readonly enAttenteGql: DemandeHabilitationStatutEnum[] =
    this.enAttenteStatuts.map(s => DemandeHabilitationStatutMapper.toGraphQl(s)!);

  private readonly actionAEffectuerGql: DemandeHabilitationStatutEnum[] =
    this.actionAEffectuerStatuts.map(s => DemandeHabilitationStatutMapper.toGraphQl(s)!);

  private readonly enAttenteServiceExterneGql: DemandeHabilitationStatutEnum[] =
    this.enAttenteServiceExterneStatuts.map(s =>
      DemandeHabilitationStatutMapper.toGraphQl(s)!
    );

  private readonly queryRefAAffecter = this.getDemandesHabilitationCountByStatutGql.watch(
    {
      variables: { statuts: this.aAffecterStatutsGql },
      ...{ fetchPolicy: 'network-only' },
    }
  );

  private readonly queryRefAControler =
    this.getDemandesHabilitationCountByStatutGql.watch({
      variables: { statuts: this.aControlerGql },
      ...{ fetchPolicy: 'network-only' },
    });

  private readonly queryRefEnAttente = this.getDemandesHabilitationCountByStatutGql.watch(
    { variables: { statuts: this.enAttenteGql }, ...{ fetchPolicy: 'network-only' } }
  );

  private readonly queryRefActionAEffectuer =
    this.getDemandesHabilitationCountByStatutGql.watch({
      variables: { statuts: this.actionAEffectuerGql },
      ...{ fetchPolicy: 'network-only' },
    });

  private readonly queryRefEnAttenteServiceExterne =
    this.getDemandesHabilitationCountByStatutGql.watch({
      variables: { statuts: this.enAttenteServiceExterneGql },
      ...{ fetchPolicy: 'network-only' },
    });

  private aAffecter$ = this.queryRefAAffecter.valueChanges.pipe(
    map(result => result.data?.demandeHabilitationsCountByStatut)
  );

  private aControler$ = this.queryRefAControler.valueChanges.pipe(
    map(result => result.data?.demandeHabilitationsCountByStatut)
  );

  private enAttente$ = this.queryRefEnAttente.valueChanges.pipe(
    map(result => result.data?.demandeHabilitationsCountByStatut)
  );

  private actionAEffectuer$ = this.queryRefActionAEffectuer.valueChanges.pipe(
    map(result => result.data?.demandeHabilitationsCountByStatut)
  );

  private enAttenteServiceExterne$ =
    this.queryRefEnAttenteServiceExterne.valueChanges.pipe(
      map(result => result.data?.demandeHabilitationsCountByStatut)
    );

  public override watchDemandesCountByCategories(): Observable<HabilitationTabsCounters> {
    const interval = 15000;

    this.queryRefAAffecter.startPolling(interval);
    this.queryRefAControler.startPolling(interval);
    this.queryRefEnAttente.startPolling(interval);
    this.queryRefActionAEffectuer.startPolling(interval);
    this.queryRefEnAttenteServiceExterne.startPolling(interval);

    return combineLatest([
      this.aAffecter$,
      this.aControler$,
      this.enAttente$,
      this.actionAEffectuer$,
      this.enAttenteServiceExterne$,
    ]).pipe(
      map(
        ([aAffecter, aControler, enAttente, actionAEffectuer, enAttenteServiceExterne]) =>
          ({
            aAffecter,
            aControler,
            actionAEffectuer,
            enAttente,
            enAttenteServiceExterne,
          }) as HabilitationTabsCounters
      )
    );
  }

  public override refreshCounts(): void {
    this.queryRefAAffecter.refetch();
    this.queryRefAControler.refetch();
    this.queryRefEnAttente.refetch();
    this.queryRefActionAEffectuer.refetch();
    this.queryRefEnAttenteServiceExterne.refetch();
  }

  public override async getDemandeHabilitations(
    search: string | null,
    statuts: HabilitationStatut[],
    gestionnaireCodes: string[],
    pageIndex: number,
    pageSize: number,
    sortField: 'dateDeclaration' | 'dateUpdate',
    sortOrder: 'asc' | 'desc'
  ): Promise<Pagination<DemandeHabilitation>> {
    const statutsGql = statuts.map(s => DemandeHabilitationStatutMapper.toGraphQl(s)!);

    const sortTypeGql: SortEnumType =
      sortOrder === 'asc' ? SortEnumType.Asc : SortEnumType.Desc;

    const sortGql: DemandeHabilitationSortInput[] =
      sortField === 'dateDeclaration'
        ? [{ dateDeclaration: sortTypeGql }, { dateUpdate: SortEnumType.Desc }]
        : [{ dateUpdate: sortTypeGql }, { dateDeclaration: SortEnumType.Desc }];

    const queryResult = await lastValueFrom(
      this.getDemandesHabilitationFilteredBackOfficeGql.fetch({
        variables: {
          take: pageSize,
          skip: pageSize * pageIndex,
          search: search,
          statuts: statutsGql,
          order: sortGql,
          gestionnaireCodes: gestionnaireCodes,
        },
        ...{ fetchPolicy: 'network-only' },
      })
    );

    const data = queryResult.data!.demandeHabilitationsFilteredBackOffice;

    const demandeHabilitations: DemandeHabilitation[] =
      data?.items?.map(i =>
        DemandeHabilitationMapper.fromGraphQL(i as DemandeHabilitationGql, true)
      ) ?? [];

    return new Pagination<DemandeHabilitation>(
      demandeHabilitations,
      pageIndex,
      pageSize,
      data!.totalCount
    );
  }

  public override async affecterDemandeHabilitation(
    publicIds: string[],
    gestionnaireId: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeHabilitationAffecterGql.mutate({
        variables: {
          gestionnaireId: gestionnaireId,
          demandeHabilitationPublicIds: publicIds,
        },
      })
    );
    this.refreshCounts();
  }

  public override async refuserDemandeHabilitation(
    publicId: string,
    commentaire: string
  ): Promise<void> {
    await lastValueFrom(
      this.demandeHabilitationRefuserGql.mutate({
        variables: {
          publicId: publicId,
          commentaire: commentaire,
        },
      })
    );
    this.refreshCounts();
  }
}
